import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, animate, useReducedMotion } from "framer-motion";
import { Scale, Gavel, FileText, Compass, Newspaper, BookOpen, Megaphone, ArrowUpRight } from "lucide-react";

export const IMPACT_META = {
  pils: { icon: Scale, to: "/pil", note: "Filed & tracked before courts" },
  orders: { icon: Gavel, to: "/pil", note: "Judicial orders on public record" },
  public_representations: { icon: Megaphone, to: "/documents", note: "Submitted to public authorities" },
  areas_of_intervention: { icon: Compass, to: "/issues", note: "Issues under the lens" },
  media_coverage: { icon: Newspaper, to: "/media#in-the-news", note: "Reports across national media" },
  documents: { icon: BookOpen, to: "/official-documents", note: "Open for public reference" },
  press_releases: { icon: FileText, to: "/media", note: "Official statements issued" },
};

const EASE = [0.215, 0.61, 0.355, 1];
const container = { hidden: {}, visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } } };
const card = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } } };

function CountUp({ value, start, testid }) {
  const reduce = useReducedMotion();
  const [n, setN] = useState(reduce ? value : 0);
  useEffect(() => {
    if (!start || reduce) { if (reduce) setN(value); return undefined; }
    const ctl = animate(0, value, { duration: 1.6, ease: [0.33, 1, 0.68, 1], onUpdate: (v) => setN(Math.round(v)) });
    return () => ctl.stop();
  }, [start, value, reduce]);
  return <span data-testid={testid} aria-hidden="true">{n.toLocaleString("en-IN")}</span>;
}

function ImpactCard({ item, index, max, inView }) {
  const meta = IMPACT_META[item.id] || IMPACT_META.documents;
  const Icon = meta.icon;
  const share = max && item.value ? Math.max(0.08, item.value / max) : 0;
  return (
    <motion.div variants={card} className="h-full">
      <Link to={meta.to} aria-label={`${item.value} ${item.label} — view section`} data-testid={`impact-card-${item.id}`}
        className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-npst-goldBorder/60 bg-white p-6 sm:p-7 shadow-[0_1px_2px_rgba(15,41,74,0.04)] transition-[transform,box-shadow,border-color] duration-300 ease-out hover:-translate-y-1 hover:border-npst-gold hover:shadow-[0_14px_34px_-10px_rgba(15,41,74,0.18)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-npst-gold focus-visible:ring-offset-2">
        <span className="pointer-events-none absolute inset-x-0 top-0 h-[3px] origin-left scale-x-0 bg-gradient-to-r from-npst-gold to-npst-goldMuted transition-transform duration-500 group-hover:scale-x-100" />
        <div className="flex items-start justify-between gap-4">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-npst-goldBorder bg-npst-goldLight text-npst-gold transition-colors duration-300 group-hover:bg-npst-navy group-hover:border-npst-navy group-hover:text-white">
            <Icon size={20} strokeWidth={1.6} />
          </div>
          <span className="font-mono text-[11px] text-npst-muted">{String(index + 1).padStart(2, "0")}</span>
        </div>
        <div className="mt-7 flex items-baseline gap-2">
          <span className="font-serif text-5xl sm:text-6xl font-semibold leading-none tracking-tight text-npst-navy">
            <CountUp value={item.value} start={inView} testid={`impact-counter-${item.id}`} />
          </span>
          <span className="mb-1 h-2 w-2 rounded-full bg-npst-gold" aria-hidden="true" />
        </div>
        <div className="mt-3 text-base font-semibold leading-snug text-npst-ink transition-colors group-hover:text-npst-navy">{item.label}</div>
        <div className="mt-1 text-xs text-npst-muted">{meta.note}</div>
        <div className="mt-6 h-[3px] w-full overflow-hidden rounded-full bg-npst-line" aria-hidden="true">
          <motion.div className="h-full origin-left rounded-full bg-npst-gold/80" initial={{ scaleX: 0 }}
            animate={{ scaleX: inView ? share : 0 }} transition={{ duration: 1.4, delay: 0.25 + index * 0.08, ease: EASE }} />
        </div>
        <div className="mt-4 flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.14em] text-npst-gold transition-colors group-hover:text-npst-navy">
          <span>View records</span>
          <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </div>
      </Link>
    </motion.div>
  );
}

export default function ImpactDashboard({ items }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const max = Math.max(...items.map((i) => Number(i.value) || 0), 1);
  const cols = items.length === 4 || items.length >= 7 ? "lg:grid-cols-4" : items.length <= 2 ? "lg:grid-cols-2" : "lg:grid-cols-3";
  return (
    <section ref={ref} className="relative overflow-hidden border-y border-npst-line bg-npst-bgAlt" data-testid="impact-dashboard-section">
      <div className="pointer-events-none absolute inset-0 opacity-[0.35] [background-image:linear-gradient(to_right,#E2D3AB33_1px,transparent_1px),linear-gradient(to_bottom,#E2D3AB33_1px,transparent_1px)] [background-size:56px_56px]" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, ease: EASE }}
          className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="label-eyebrow text-npst-gold" data-testid="impact-dashboard-badge">Impact Dashboard</div>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-npst-ink" data-testid="impact-dashboard-heading">
              The Trust's public record, <span className="text-npst-navy">at a glance</span>
            </h2>
            <p className="mt-4 text-sm sm:text-base leading-relaxed text-npst-inkSoft" data-testid="impact-dashboard-subheading">
              Live figures drawn directly from our archive. Each figure opens the records behind it.
            </p>
          </div>
          <div className="flex items-center gap-3 self-start rounded-full border border-npst-goldBorder bg-white/80 px-4 py-2 backdrop-blur-sm lg:self-auto" data-testid="impact-dashboard-total">
            <span className="inline-flex h-2 w-2 rounded-full bg-npst-gold" />
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-npst-navy">Updated live from the archive</span>
          </div>
        </motion.div>
        <motion.div variants={container} initial="hidden" animate={inView ? "visible" : "hidden"}
          className={`grid grid-cols-1 gap-5 sm:grid-cols-2 lg:gap-6 ${cols}`} data-testid="impact-dashboard-grid">
          {items.map((it, i) => <ImpactCard key={it.id} item={{ ...it, value: Number(it.value) || 0 }} index={i} max={max} inView={inView} />)}
        </motion.div>
      </div>
    </section>
  );
}
